// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { SvgIconName } from '@shared/ui-kit/components/app-icon/app-icon';

export type DocumentFileKind = 'document' | 'image' | 'spreadsheet';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
]);
const SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xls', 'csv', 'ods']);

export const getFileExtension = (fileName: string): string => {
  const trimmed = fileName.trim();
  const lastDotIndex = trimmed.lastIndexOf('.');

  if (lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1) {
    return '';
  }

  return trimmed.slice(lastDotIndex + 1).toLowerCase();
};

export const getDocumentFileKind = (fileName: string): DocumentFileKind => {
  const extension = getFileExtension(fileName);

  if (IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }

  if (SPREADSHEET_EXTENSIONS.has(extension)) {
    return 'spreadsheet';
  }

  return 'document';
};

export const getDocumentFileIcon = (fileName: string): SvgIconName => {
  const kind = getDocumentFileKind(fileName);

  if (kind === 'image') {
    return 'FileImage';
  }

  if (kind === 'spreadsheet') {
    return 'Sheet';
  }

  return 'FileText';
};

export const formatFileTypeLabel = (fileName: string): string => {
  const extension = getFileExtension(fileName);

  return extension ? extension.toUpperCase() : 'FILE';
};

export const splitFileNameForDisplay = (
  fileName: string,
): { baseName: string; extensionSuffix: string } => {
  const extension = getFileExtension(fileName);

  if (!extension) {
    return { baseName: fileName, extensionSuffix: '' };
  }

  const extensionSuffix = `.${extension}`;

  if (!fileName.toLowerCase().endsWith(extensionSuffix.toLowerCase())) {
    return { baseName: fileName, extensionSuffix: '' };
  }

  return {
    baseName: fileName.slice(0, -extensionSuffix.length),
    extensionSuffix,
  };
};
